// src/components/farm/FarmHeader.js
// Top header for the Farm marketplace page.
import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, MapPin, ShoppingBag } from 'lucide-react';
import { AGRICULTURAL_CATEGORIES } from '../../utils/agriculturalCategories';
import { ANIMAL_CATEGORIES } from '../../utils/animalCategories';
import FarmSecondaryNav from '../navigation/FarmSecondaryNav';
import { FilterChip } from '../ui/FilterChip';

export default function FarmHeader({
  category,
  setCategory,
  nearbyActive,
  setNearbyActive,
  searchTerm,
  setSearchTerm,
  searchOpen,
  setSearchOpen,
}) {
  const { t } = useTranslation('farmMarketplace');
  const navigate = useNavigate();
  const inputRef = useRef(null);

  // Auto-focus the search input when opened
  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  // Top categories to show as chips (first 12 crops + animal categories).
  // Labels are translated via i18next so the same component renders in
  // English, Kinyarwanda, and any future language without hardcoded text.
  const categoryChips = [
    ...AGRICULTURAL_CATEGORIES.slice(0, 12).map((cat) => ({
      id: cat.id,
      name: t(`cropCatalog:categories.${cat.id}`, { defaultValue: cat.name }),
    })),
...ANIMAL_CATEGORIES.map((cat) => ({
        id: cat.id,
        name: t(`farmMarketplace:secondaryNav.animalCategories.${cat.id}`, { defaultValue: cat.name }),
      })),
  ];

  return (
    <header className="sticky top-0 z-30 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
      {/* Row 1: Title + action icons (mobile only) */}
      <div className="lg:hidden max-w-7xl mx-auto px-3 sm:px-4 pt-3 pb-2.5 flex items-center justify-between gap-2">
        {searchOpen ? (
          <div className="flex items-center flex-1 gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                ref={inputRef}
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('searchPlaceholder')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-neutral-700 rounded-full bg-white dark:bg-neutral-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                aria-label={t('searchPlaceholder')}
              />
            </div>
            <button
              onClick={() => { setSearchOpen(false); setSearchTerm(''); }}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label={t('close', { ns: 'common' })}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('title')}
            </h1>
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate('/my-offers')}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-gray-300 transition-colors"
                aria-label={t('offerManager')}
                title={t('offerManager')}
              >
                <ShoppingBag className="w-5 h-5" />
              </button>
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-gray-300 transition-colors"
                aria-label={t('searchPlaceholder')}
              >
                <Search className="w-5 h-5" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Row 2: Filters (horizontal scroll) */}
      <div className="lg:hidden max-w-7xl mx-auto px-3 sm:px-4 pb-3 border-t border-gray-100 dark:border-neutral-800/70">
        <div className="flex gap-2.5 overflow-x-auto no-scrollbar pt-2 pb-1 -mx-1 px-1">

          {/* Nearby */}
          <FilterChip
            label={t('nearby')}
            icon={<MapPin className="w-3.5 h-3.5" />}
            active={nearbyActive}
            onClick={() => setNearbyActive((v) => !v)}
          />

          {/* All */}
          <FilterChip
            label={t('allCategories')}
            active={!category}
            onClick={() => setCategory('')}
          />

          {/* Category chips from agriculturalCategories */}
          {categoryChips.map((cat) => (
            <FilterChip
              key={cat.id}
              label={cat.name}
              active={category === cat.id}
              onClick={() =>
                setCategory((prev) => (prev === cat.id ? '' : cat.id))
              }
            />
          ))}
        </div>
      </div>

      <FarmSecondaryNav
        category={category}
        setCategory={setCategory}
        nearbyActive={nearbyActive}
        setNearbyActive={setNearbyActive}
        setSearchTerm={setSearchTerm}
        searchTerm={searchTerm}
        t={t}
      />
    </header>
  );
}
